import { useEffect, useState } from "react";
import { deleteNote, getNote, updateNote } from "../services/api";

const NoteDetail = ({
  authToken,
  leadId,
  noteId,
  onNoteUpdated,
  onNoteDeleted,
  onClose,
}) => {
  const [note, setNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNote = async () => {
      console.log("Fetching with:", { leadId, noteId, authToken });
      if (!noteId || !leadId) {
        setError("Note ID and Lead ID required");
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const fetchedNote = await getNote(authToken, leadId, noteId);
        console.log("Fetched note data:", fetchedNote);
        if (!fetchedNote || Object.keys(fetchedNote).length === 0) {
          setError("Note data is empty or invalid");
          return;
        }
        setNote(fetchedNote);
        setEditedContent(fetchedNote.content || "");
      } catch (error) {
        console.error("Error fetching note:", error);
        setError("Failed to fetch note. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchNote();
  }, [authToken, leadId, noteId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(note.content);
  };

  const handleSave = async () => {
    if (!editedContent.trim()) {
      setError("Note content cannot be empty");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const updatedNote = await updateNote(authToken, noteId, {
        content: editedContent,
      });
      setNote(updatedNote);
      setIsEditing(false);
      if (onNoteUpdated) onNoteUpdated(updatedNote);
    } catch (error) {
      console.error("Error updating note:", error);
      setError("Failed to update note. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      setIsLoading(true);
      setError(null);
      try {
        await deleteNote(authToken, noteId);
        if (onNoteDeleted) onNoteDeleted(noteId);
      } catch (error) {
        console.error("Error deleting note:", error);
        setError("Failed to delete note. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    console.log("NoteDetail is in loading state");
    return <div className="mt-4">Loading...</div>;
  }
  if (error) {
    console.log("NoteDetail has an error:", error);
    return <div className="mt-4 text-red-500">{error}</div>;
  }
  if (!note) {
    console.log("NoteDetail: note is null or undefined");
    return <div className="mt-4 text-red-500">No note data available</div>;
  }
  console.log("NoteDetail rendering note:", note);

  return (
    <div className="bg-white shadow-sm rounded-lg p-6 hover:border hover:border-teal-200 transition duration-300">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Note Details</h3>
        {!isEditing && (
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 bg-secondary text-white rounded-lg hover:bg-text-primary focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
            <button
              onClick={handleEdit}
              className="bg-secondary hover:bg-secondary/90/85 text-white font-medium py-1.5 px-3 rounded-lg focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-1.5 px-3 rounded-lg focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-300"
            >
              Delete
            </button>
          </div>
        )}
      </div>
      {isEditing ? (
        <div>
          <textarea
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300 text-gray-700 text-sm"
            rows="4"
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
          ></textarea>
          <div className="mt-3 flex space-x-3">
            <button
              onClick={handleSave}
              className="bg-secondary hover:bg-secondary/90/85 text-white font-medium py-2 px-4 rounded-lg focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancelEdit}
              className="bg-background0 hover:bg-background/110-600 text-white font-medium py-2 px-4 rounded-lg focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="ph-mask text-gray-700 text-sm whitespace-pre-wrap">
            {note.content}
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Created by: {note.created_by} on{" "}
            {new Date(note.created_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default NoteDetail;